import { useFullscreenPortal } from './chunk-MBTLYAMU.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React2 from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Check, X, Copy } from 'lucide-react';

var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
function TooltipHint({ label, children }) {
  return /* @__PURE__ */ jsx(TooltipProvider, { delayDuration: 400, children: /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children }),
    /* @__PURE__ */ jsx(TooltipContent, { children: label })
  ] }) });
}
var TooltipContent = React2.forwardRef(function TooltipContent2({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) {
  const portalContainer = useFullscreenPortal();
  return /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx(
    TooltipPrimitive.Content,
    {
      ref,
      sideOffset,
      collisionPadding,
      className: cn(
        "z-50 max-w-[min(20rem,calc(100vw-1rem),var(--radix-tooltip-content-available-width,100vw))] [overflow-wrap:anywhere] overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95",
        className
      ),
      ...props
    }
  ) });
});
async function writeClipboardText(value) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
    }
  }
  return legacyCopy(value);
}
function legacyCopy(value) {
  if (typeof document === "undefined" || !document.body || typeof document.execCommand !== "function") return false;
  const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const selection = document.getSelection();
  const ranges = selection ? Array.from({ length: selection.rangeCount }, (_, i) => selection.getRangeAt(i)) : [];
  const area = document.createElement("textarea");
  area.value = value;
  area.setAttribute("readonly", "");
  area.setAttribute("aria-hidden", "true");
  area.style.cssText = "position:fixed;top:0;left:-9999px;opacity:0;font-size:12pt;";
  document.body.appendChild(area);
  area.focus({ preventScroll: true });
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  if (selection) {
    selection.removeAllRanges();
    for (const range of ranges) selection.addRange(range);
  }
  focused?.focus({ preventScroll: true });
  return ok;
}
var FAILED_MS = 5e3;
function useCopyToClipboard(resetMs = 2e3) {
  const [status, setStatus] = React2.useState("idle");
  const timer = React2.useRef(null);
  React2.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  const copy = React2.useCallback(
    async (value) => {
      const ok = await writeClipboardText(value);
      setStatus(ok ? "copied" : "failed");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setStatus("idle"), ok ? resetMs : Math.max(resetMs, FAILED_MS));
      return ok;
    },
    [resetMs]
  );
  return { copied: status === "copied", failed: status === "failed", status, copy };
}
function copyShortcut() {
  if (typeof navigator === "undefined") return "Ctrl+C";
  return /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent) ? "\u2318C" : "Ctrl+C";
}
function defaultLabel(value) {
  return value.length > 80 ? "Copy to clipboard" : `Copy ${value}`;
}
var ICON_CLASS = {
  3: "size-3",
  3.5: "size-3.5",
  4: "size-4"
};
function CopyButton({
  value,
  label,
  className,
  iconSize = 3
}) {
  const { status, copy } = useCopyToClipboard();
  const [hovered, setHovered] = React2.useState(false);
  const name = label ?? defaultLabel(value);
  const icon = ICON_CLASS[iconSize];
  const message = status === "copied" ? "Copied" : status === "failed" ? `Couldn't copy \u2014 select it and press ${copyShortcut()}` : label ?? "Copy";
  return (
    // Its own provider: a copy button is dropped into tables, dialogs and
    // packages that mount no app shell, and the result must show everywhere.
    /* @__PURE__ */ jsx(TooltipPrimitive.Provider, { delayDuration: 500, children: /* @__PURE__ */ jsxs(TooltipPrimitive.Root, { open: hovered || status !== "idle", onOpenChange: setHovered, children: [
      /* @__PURE__ */ jsxs(
        TooltipPrimitive.Trigger,
        {
          type: "button",
          onClick: (e) => {
            e.stopPropagation();
            void copy(value);
          },
          "aria-label": name,
          "data-copy-status": status,
          className: cn(
            // The icon is 12px; the ::after widens the hit area to 20px
            // without moving anything beside it.
            "relative inline-flex items-center rounded-sm text-muted-foreground transition-colors hover:text-foreground",
            "after:absolute after:-inset-1 after:content-['']",
            "focus-ring",
            status === "copied" && "text-success hover:text-success",
            status === "failed" && "text-destructive hover:text-destructive",
            className
          ),
          children: [
            status === "copied" ? /* @__PURE__ */ jsx(Check, { className: icon }) : status === "failed" ? /* @__PURE__ */ jsx(X, { className: icon }) : /* @__PURE__ */ jsx(Copy, { className: icon }),
            /* @__PURE__ */ jsx("span", { className: "sr-only", "aria-live": "polite", children: status === "copied" ? "Copied" : status === "failed" ? "Copy failed" : "" })
          ]
        }
      ),
      /* @__PURE__ */ jsx(TooltipContent, { side: "top", className: "px-2 py-1", children: message })
    ] }) })
  );
}

export { CopyButton, Tooltip, TooltipContent, TooltipHint, TooltipProvider, TooltipTrigger, useCopyToClipboard, writeClipboardText };
//# sourceMappingURL=chunk-DIDPLGSY.js.map
//# sourceMappingURL=chunk-DIDPLGSY.js.map